/**
 * Prefijo de ruta del ambiente. Es lo ÚNICO que sabe dónde está montada la app.
 *
 * Por qué existe: el portal se sirve en la raíz del dominio en QA y en PROD, pero el ambiente de
 * DESARROLLO vive bajo `https://culture-qa.sigmafoods.com/dev` (decisión del cliente, 2026-09-05:
 * el portal usa `/dev`, mientras que las APIs siguen bajo `/development`).
 *
 * `import.meta.env.BASE_URL` lo pone Vite a partir del `base` del build (`VITE_BASE` en el
 * pipeline). `azure-pipelines.next.yml` y `azure-pipelines.latest.yml` lo fijan a `'/'`, así que en
 * QA y PROD `BASE_PATH` es `''` y `withBase`/`asset` son la IDENTIDAD: este fichero no cambia su
 * comportamiento. En vitest `BASE_URL` vale `'/'`, así que las pruebas existentes tampoco cambian.
 *
 * 🔴 Cuándo usarlos, y cuándo NO:
 *   - `<BrowserRouter basename={BASE_PATH}>` ya prefija TODO lo que pasa por react-router:
 *     `navigate('/app/...')`, `<Navigate to>`, `<Link>`, `<NavLink>`. Esas rutas se siguen
 *     escribiendo SIN prefijo y no hay que tocarlas.
 *   - `withBase()` es para lo que NO pasa por el router: `window.location.assign/replace/href` y
 *     los `<a href>` nativos. Sin prefijo, el usuario se sale del ambiente y aterriza en QA sin
 *     ningún aviso.
 *   - `asset()` es para las rutas de ficheros de `public/` escritas como cadena en el código: Vite
 *     reescribe las de `index.html`, pero NO las del runtime (medido 2026-09-05).
 *
 * 🔑 Por qué la lógica vive en `crearHelpers(baseUrl)` y no lee `import.meta.env` directamente:
 * para poder probarla con cualquier base SIN `vi.stubEnv`. Se intentó al revés y el `stubEnv` se
 * filtraba a otros ficheros de prueba del mismo worker, tumbando una prueba ajena
 * (`adminWinners`) que sólo fallaba en la suite completa y pasaba en aislamiento — de las que
 * cuestan una tarde. Con una función pura, la prueba no toca estado global.
 */

export interface HelpersDeBase {
  /** `''` en QA/PROD, `'/dev'` en desarrollo. Sin barra final, para poder concatenar. */
  BASE_PATH: string;
  /** Prefija una ruta de la app para navegaciones de documento y `<a href>`. */
  withBase: (ruta: string) => string;
  /** Prefija la ruta de un fichero servido desde `public/`. Acepta con o sin barra inicial. */
  asset: (ruta: string) => string;
  /**
   * Como `asset()`, pero para un valor que viene de un DATO (Mongo, API) y no del código, así que
   * puede ser cualquier cosa: una URL absoluta, un data-URI o una ruta del sitio. Sólo prefija la
   * última. Ver `assetPersistido` más abajo para el porqué.
   */
  assetPersistido: (valor: string) => string;
  /** Quita el prefijo de un `pathname` para compararlo con rutas del router. */
  sinBase: (pathname: string) => string;
}

/** Fábrica pura: toda la lógica del prefijo, parametrizada por el `base` del build. */
export function crearHelpers(baseUrl: string): HelpersDeBase {
  const BASE_PATH = baseUrl.replace(/\/$/, '');
  const asset = (ruta: string) => `${baseUrl}${ruta.replace(/^\//, '')}`;
  return {
    BASE_PATH,
    // La raíz conserva su barra: `withBase('/')` -> `'/'` en QA/PROD y `'/dev/'` en dev.
    withBase: (ruta) => (ruta === '/' ? baseUrl : `${BASE_PATH}${ruta}`),
    asset,
    // 🔴 Para valores GUARDADOS (iconos elegidos en el admin, imágenes de catálogo). A diferencia de
    // `asset()`, aquí no se sabe qué va a llegar: el admin puede pegar `https://...`, subir un PNG
    // que se guarda como `data:image/...`, o elegir un asset del propio sitio (`/assets/x.svg`).
    // Sólo el último caso lleva prefijo; envolver los otros produciría `/dev/https://...` y rompería
    // la imagen. Se prefija AL RENDERIZAR y NO se migra la base a propósito: el mismo documento
    // tiene que servir en QA, dev y PROD, que están montados en rutas distintas.
    assetPersistido: (valor) => {
      // Cualquier esquema (`http:`, `https:`, `data:`, `blob:`) o protocol-relative (`//host`).
      if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(valor)) return valor;
      // Relativa al documento (`x.svg`, `./x.svg`): ya resuelve dentro del ambiente, no se toca.
      return valor.startsWith('/') ? asset(valor) : valor;
    },
    sinBase: (pathname) =>
      BASE_PATH && pathname.startsWith(BASE_PATH) ? pathname.slice(BASE_PATH.length) || '/' : pathname,
  };
}

const helpers = crearHelpers(import.meta.env.BASE_URL);

export const BASE_PATH = helpers.BASE_PATH;
export const withBase = helpers.withBase;
export const asset = helpers.asset;
export const assetPersistido = helpers.assetPersistido;
export const sinBase = helpers.sinBase;
